import { HttpLink } from '@apollo/client'
import { parseJsonIfValid } from '@mbfab/shared'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'

import { envVariables } from '../../lib/env'
import { notifyBugsnag } from '../bugsnag'
import { getUserInfoFromLocalStorage } from '../getUserInfoFromLocalStorage'
import { logger } from '../logger'
import { LOGGER_EVENTS } from '../logger_events'

class ApolloClientFetchError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientFetchError'
  }
}

class ApolloClientHttpStatusNotOkError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'ApolloClientHttpStatusNotOkError'
  }
}

const API_HOST = envVariables.API_HOST
export const httpLink = new HttpLink({
  uri: `${API_HOST}/graphql`,
  credentials: 'include',
  headers: {
    // NOTE: JWTを使って認証をするため、cookieは基本的に使わない。
    // integrationの設定時にOAuthのアクセストークンをサーバーサイドに渡すときのみセッションを用いるため必要。
    'Access-Control-Allow-Credentials': 'true',
  },
  async fetch(uri, options) {
    const requestId = uuidv4()
    const body = z.string().safeParse(options?.body ?? '')
    const maybeJson = parseJsonIfValid(body.success ? body.data : '')
    const graphqlBody = z.object({ operationName: z.string(), variables: z.unknown() }).safeParse(maybeJson ?? {})
    const operationName = graphqlBody.success ? graphqlBody.data.operationName : `(not graphql operation)`
    const variables = graphqlBody.success ? graphqlBody.data.variables : undefined
    const userInfo = getUserInfoFromLocalStorage()
    const userName =
      userInfo === undefined ? `未ログインユーザー` : `${userInfo.name}(${userInfo.email}, ${userInfo.id})`
    const loggerBody = {
      type: 'fetch',
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      uri: uri.toString(),
      operationName,
      user: userInfo ?? null,
      variables,
      raw: JSON.stringify({
        // header.authorizationにJWTが含まれるので、これをログに残すのはやめること
        body: options?.body,
        method: options?.method,
      }),
      requestId,
    }

    try {
      void logger?.info(LOGGER_EVENTS.APOLLO_CLIENT, `${operationName} ${userName}`, loggerBody)
      const startAt = dayjs()
      const res = await fetch(uri, {
        ...options,
        headers: { ...options?.headers, 'X-REQUEST-ID': requestId },
      })
      const duration = -startAt.diff()
      const body = {
        ...loggerBody,
        type: 'fetch finished',
        duration,
        res: {
          status: res.status,
          statusText: res.statusText,
          // NOTE: 調査用にstaging環境ではLogtailにbodyも含めて出力する、
          // stagingという値をNODE_ENVで使いたいがadhoc度が高まるためこのようにしている
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          body: envVariables.NODE_ENV !== 'production' ? (res.body?.toString() ?? null) : '',
        },
      }
      if (res.status !== 200) {
        notifyBugsnag({
          error: new ApolloClientHttpStatusNotOkError(`http status: ${res.status}, statusText: ${res.statusText}`),
        })
        void logger?.error(
          LOGGER_EVENTS.APOLLO_CLIENT,
          `${duration}ms ${operationName} ${userName} / status: ${res.status}, statusText: ${res.statusText}`,
          body,
        )
      } else {
        void logger?.info(
          LOGGER_EVENTS.APOLLO_CLIENT,
          `${duration}ms ${operationName} ${userName} / status: ${res.status}, statusText: ${res.statusText}`,
          body,
        )
      }
      return res
    } catch (e) {
      const metadata = { key: `fetch`, value: { requestId, options: loggerBody.raw } }
      if (e instanceof Error) {
        if (e.name === 'AbortError') {
          // ページ遷移などで中断されるとAbortErrorが発生するが、この場合は挙動に悪影響はないので通知はせずそのままエラーを返す
          throw e
        }
        notifyBugsnag({ error: new ApolloClientFetchError(`${e.name}: ${e.message}`), metadata })
        void logger?.error(
          LOGGER_EVENTS.APOLLO_CLIENT,
          `error ${operationName} ${userName} / ${e.name}: ${e.message}`,
          {
            ...loggerBody,
            error: {
              name: e.name,
              message: e.message,
            },
          },
        )
      } else {
        notifyBugsnag({ error: new ApolloClientFetchError(`fetch error occured`), metadata })
        void logger?.error(
          LOGGER_EVENTS.APOLLO_CLIENT,
          `error ${operationName} / [is not instnace of Error]`,
          loggerBody,
        )
      }

      throw e
    }
  },
})
